import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import CarrierLookupCard from "@/components/CarrierLookupCard";
import {
  ShieldCheck, Activity, AlertTriangle, Plus, Trash2, Edit3,
  CheckCircle2, MapPin, Calendar, FileWarning, Car, UserX, TrendingDown,
} from "lucide-react";

const BASIC_LABELS = {
  unsafe_driving: "Unsafe Driving",
  hours_of_service: "Hours of Service",
  driver_fitness: "Driver Fitness",
  controlled_substances: "Controlled Subst. / Alcohol",
  vehicle_maintenance: "Vehicle Maintenance",
  hazmat: "HazMat Compliance",
  crash_indicator: "Crash Indicator",
};

const VIOL_STATUS_TAG = {
  open: "hs-tag-warn",
  paid: "hs-tag-success",
  contested: "hs-tag-info",
  dismissed: "hs-tag-success",
};

const CHALLENGE_TAG = {
  not_challenged: "hs-tag-info",
  pending: "hs-tag-warn",
  won: "hs-tag-success",
  lost: "hs-tag-danger",
  withdrawn: "hs-tag-info",
};

const SEVERITY_TAG = {
  minor: "hs-tag-info",
  moderate: "hs-tag-warn",
  severe: "hs-tag-danger",
};

export default function Safety({ adminClientId }) {
  const { user } = useAuth();
  const isAdminView = !!adminClientId || user?.role === "admin" || user?.role === "super_admin";
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const targetParam = adminClientId ? `?client_id=${adminClientId}` : "";

  const [score, setScore] = useState(null);
  const [insps, setInsps] = useState([]);
  const [viols, setViols] = useState([]);
  const [accs, setAccs] = useState([]);
  const [riskRows, setRiskRows] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInspModal, setShowInspModal] = useState(false);
  const [showViolModal, setShowViolModal] = useState(false);
  const [showAccModal, setShowAccModal] = useState(false);
  const [editingInsp, setEditingInsp] = useState(null);
  const [editingViol, setEditingViol] = useState(null);
  const [editingAcc, setEditingAcc] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, i, v, a, r, d] = await Promise.all([
        api.get(`/safety/score${targetParam}`),
        api.get(`/inspections${targetParam}`),
        api.get(`/violations${targetParam}`),
        api.get(`/accidents${targetParam}`),
        api.get(`/safety/driver-risk${targetParam}`),
        api.get(`/drivers${targetParam}`),
      ]);
      setScore(s.data); setInsps(i.data); setViols(v.data);
      setAccs(a.data); setRiskRows(r.data); setDrivers(d.data);
    } finally { setLoading(false); }
  }, [targetParam]);

  useEffect(() => { load(); }, [load]);

  const removeInsp = async (id) => {
    if (!window.confirm("Delete this inspection?")) return;
    await api.delete(`/inspections/${id}`);
    load();
  };
  const removeViol = async (id) => {
    if (!window.confirm("Delete this violation?")) return;
    await api.delete(`/violations/${id}`);
    load();
  };
  const removeAcc = async (id) => {
    if (!window.confirm("Delete this accident record?")) return;
    await api.delete(`/accidents/${id}`);
    load();
  };
  const updateViolStatus = async (v, status) => {
    await api.put(`/violations/${v.id}`, { ...v, status });
    toast.success(`Marked ${status}`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="safety-page">
      {!adminClientId && (
        <div>
          <div className="hs-overline text-blue-700">SAFETY & MONITORING</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter mt-2">Safety</h1>
          <p className="text-sm text-slate-500 mt-2 max-w-2xl">
            {isAdminView
              ? "Your official FMCSA carrier snapshot — plus the inspections, citations, accidents and driver risk you log internally."
              : "Inspections, citations, accidents and driver risk you log internally."}
          </p>
        </div>
      )}

      {/* FMCSA SAFER lookup — OFFICIAL DATA (admin / super-admin only) */}
      {isAdminView && (
        <CarrierLookupCard />
      )}

      {/* FMCSA-REPORTED stats from CSA snapshot — official numbers from data.transportation.gov */}
      {score?.fmcsa_reported && (
        <div className="hs-card p-6" data-testid="safety-fmcsa-reported">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div>
              <div className="hs-overline text-blue-700">FMCSA REPORTED · DATA.TRANSPORTATION.GOV</div>
              <h3 className="font-display font-bold text-xl mt-1">Government Carrier Snapshot</h3>
              <p className="text-[11px] text-slate-500 mt-0.5">
                Last 24 months · Snapshot month{" "}
                <span className="font-mono">{score.fmcsa_reported.snapshot_month || "—"}</span>
              </p>
            </div>
            {score.fmcsa_reported.safety_rating && (
              <span className={`hs-tag ${score.fmcsa_reported.safety_rating?.toLowerCase().includes('sat') ? 'hs-tag-success' : 'hs-tag-warn'}`}>
                {score.fmcsa_reported.safety_rating}
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Stat icon={Activity} label="Inspections (24mo)"
                  value={score.fmcsa_reported.inspections_24mo ?? 0} accent="#1D4ED8" />
            <Stat icon={UserX} label="Driver OOS"
                  value={score.fmcsa_reported.drivers_oos_24mo ?? 0}
                  accent={(score.fmcsa_reported.drivers_oos_24mo || 0) ? "#DC2626" : "#059669"} />
            <Stat icon={Car} label="Vehicle OOS"
                  value={score.fmcsa_reported.vehicles_oos_24mo ?? 0}
                  accent={(score.fmcsa_reported.vehicles_oos_24mo || 0) ? "#DC2626" : "#059669"} />
            <Stat icon={AlertTriangle} label="Crashes (24mo)"
                  value={score.fmcsa_reported.crashes_24mo ?? 0}
                  accent={(score.fmcsa_reported.crashes_24mo || 0) ? "#DC2626" : "#059669"} />
          </div>
          {(score.fmcsa_reported.basics_in_alert || []).length > 0 && (
            <div className="mt-4 bg-rose-50 border-l-2 border-rose-600 p-3">
              <div className="hs-overline text-rose-700 text-[10px]">FMCSA ALERTS</div>
              <div className="mt-1 text-sm">
                FMCSA has flagged the carrier on{" "}
                {score.fmcsa_reported.basics_in_alert.map((k) => BASIC_LABELS[k] || k).join(", ")}.
              </div>
            </div>
          )}
          <p className="text-[10px] text-slate-400 mt-3">
            Source: U.S. DOT FMCSA Safety Measurement System · refreshes monthly.
            Click <span className="font-mono">/admin/csa → Refresh All Clients Now</span> to pull the latest data.
          </p>
        </div>
      )}

      {/* If no FMCSA snapshot loaded yet, prompt admin to fetch */}
      {score && !score.fmcsa_reported && isAdmin && (
        <div className="hs-card p-5 border-l-4 border-amber-500 bg-amber-50/50" data-testid="safety-fmcsa-empty">
          <div className="flex items-start gap-3">
            <AlertTriangle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-bold text-sm">No FMCSA snapshot loaded for this carrier yet</div>
              <p className="text-xs text-slate-600 mt-1">
                Either this client has no DOT number on file, or the monthly refresh hasn&rsquo;t pulled their data.
                Go to <span className="font-mono">/admin/csa</span> and click{" "}
                <strong>REFRESH ALL CLIENTS NOW</strong> to pull official FMCSA stats for every client at once.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Internal compliance snapshot — clearly labelled as derived from logged data */}
      <div className="grid lg:grid-cols-3 gap-4">
        {/* Score */}
        <div className="hs-card p-6 flex flex-col items-center justify-center text-center" data-testid="safety-score-card">
          <div className="hs-overline">INTERNAL COMPLIANCE SCORE</div>
          <div className="text-[10px] text-slate-500 mt-1">computed from your logged data</div>
          <ScoreRing value={score?.score ?? 0} grade={score?.grade ?? "—"} />
          <div className="mt-4 text-xs text-slate-500 font-mono">
            UPDATED {(score?.computed_at || "").slice(0, 10)}
          </div>
        </div>

        {/* Stats */}
        <div className="hs-card p-6 lg:col-span-2">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="hs-overline">YOUR INTERNAL LOG</div>
              <p className="text-[11px] text-slate-500 mt-0.5">From inspections / violations / filings you&rsquo;ve logged in this app</p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4">
            <Stat icon={Activity} label="Inspections (12mo)" value={score?.stats?.inspections_12mo ?? 0} accent="#1D4ED8" />
            <Stat icon={AlertTriangle} label="OOS" value={score?.stats?.oos_inspections ?? 0} accent={(score?.stats?.oos_inspections || 0) ? "#DC2626" : "#059669"} />
            <Stat icon={FileWarning} label="Open Violations" value={score?.stats?.open_violations ?? 0} accent="#D97706" />
            <Stat icon={Calendar} label="Expiring 30d" value={score?.stats?.expiring_filings_30d ?? 0} accent="#D97706" />
          </div>
          <div className="mt-6">
            <div className="hs-overline mb-1">CITATIONS BY CATEGORY</div>
            <div className="text-[11px] text-slate-500 mb-3">
              Derived from your logged violations only — <span className="font-bold">NOT</span> your official FMCSA CSA BASIC percentile scores (those aren&rsquo;t in the free FMCSA API — see snapshot above).
            </div>
            <div className="space-y-2">
              {score && Object.entries(score.basics).map(([k, v]) => <Bar key={k} label={BASIC_LABELS[k]} value={v} testid={`basic-${k}`} />)}
            </div>
          </div>
        </div>
      </div>

      {/* Inspections */}
      <div className="hs-card">
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <div>
            <div className="hs-overline">DOT ROADSIDE</div>
            <h3 className="font-display font-bold text-xl mt-1">Inspections ({insps.length})</h3>
          </div>
          {isAdmin && (
            <button onClick={() => { setEditingInsp(null); setShowInspModal(true); }} className="hs-btn-primary inline-flex items-center gap-2" data-testid="add-inspection-button">
              <Plus size={14}/> LOG INSPECTION
            </button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-left">
              <tr>
                <Th>Date</Th><Th>Level</Th><Th>Location</Th><Th>Violations</Th><Th>OOS</Th><Th>Score</Th><Th>Notes</Th><Th></Th>
              </tr>
            </thead>
            <tbody>
              {insps.map((i) => (
                <tr key={i.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`inspection-row-${i.id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{(i.date || "").slice(0, 10)}</td>
                  <td className="px-4 py-3"><span className="hs-tag hs-tag-info">L{i.level}</span></td>
                  <td className="px-4 py-3 inline-flex items-center gap-1"><MapPin size={12} className="text-slate-400"/> {i.location}</td>
                  <td className="px-4 py-3 font-mono">{i.violations_count}</td>
                  <td className="px-4 py-3">
                    {i.oos
                      ? <span className="hs-tag hs-tag-danger">OOS</span>
                      : <span className="hs-tag hs-tag-success">CLEAR</span>}
                  </td>
                  <td className="px-4 py-3 font-mono">{i.score}</td>
                  <td className="px-4 py-3 text-xs text-slate-500 max-w-xs truncate">{i.notes}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {isAdmin && (
                      <>
                        <button onClick={() => { setEditingInsp(i); setShowInspModal(true); }} className="hs-btn-ghost p-2" title="Edit"><Edit3 size={13}/></button>
                        <button onClick={() => removeInsp(i.id)} className="hs-btn-ghost p-2" title="Delete"><Trash2 size={13}/></button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {insps.length === 0 && !loading && (
                <tr><td colSpan={8} className="p-8 text-center text-sm text-slate-500">No inspections logged.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Violations */}
      <div className="hs-card">
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <div>
            <div className="hs-overline">CITATIONS</div>
            <h3 className="font-display font-bold text-xl mt-1">Violations ({viols.length})</h3>
          </div>
          {isAdmin && (
            <button onClick={() => { setEditingViol(null); setShowViolModal(true); }} className="hs-btn-primary inline-flex items-center gap-2" data-testid="add-violation-button">
              <Plus size={14}/> LOG VIOLATION
            </button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-left">
              <tr>
                <Th>Date</Th><Th>Code</Th><Th>Description</Th><Th>BASIC</Th><Th>Severity</Th><Th>Fine</Th><Th>Status</Th><Th>Challenge</Th><Th></Th>
              </tr>
            </thead>
            <tbody>
              {viols.map((v) => (
                <tr key={v.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`violation-row-${v.id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{(v.date || "").slice(0, 10)}</td>
                  <td className="px-4 py-3 font-mono">{v.code}</td>
                  <td className="px-4 py-3">{v.description}</td>
                  <td className="px-4 py-3 text-xs uppercase">{v.basic?.replace("_", " ")}</td>
                  <td className="px-4 py-3"><span className={`hs-tag ${SEVERITY_TAG[v.severity] || "hs-tag-info"}`}>{(v.severity || "minor").toUpperCase()}</span></td>
                  <td className="px-4 py-3 font-mono">${(v.fine || 0).toFixed(2)}</td>
                  <td className="px-4 py-3"><span className={`hs-tag ${VIOL_STATUS_TAG[v.status] || "hs-tag-warn"}`}>{(v.status || "open").toUpperCase()}</span></td>
                  <td className="px-4 py-3 text-xs">
                    {v.challenged ? (
                      <span className={`hs-tag ${CHALLENGE_TAG[v.challenge_status] || "hs-tag-info"}`} data-testid={`viol-challenge-${v.id}`}>
                        {(v.challenge_status || "pending").replace("_", " ").toUpperCase()}
                      </span>
                    ) : (
                      <span className="text-slate-400 font-mono text-[10px]">— NOT CHALLENGED</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {isAdmin && (
                      <>
                        {v.status === "open" && (
                          <button onClick={() => updateViolStatus(v, "paid")} className="hs-btn-ghost p-2 text-emerald-600" title="Mark paid" data-testid={`violation-pay-${v.id}`}>
                            <CheckCircle2 size={13}/>
                          </button>
                        )}
                        <button onClick={() => { setEditingViol(v); setShowViolModal(true); }} className="hs-btn-ghost p-2" title="Edit"><Edit3 size={13}/></button>
                        <button onClick={() => removeViol(v.id)} className="hs-btn-ghost p-2" title="Delete"><Trash2 size={13}/></button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {viols.length === 0 && !loading && (
                <tr><td colSpan={9} className="p-8 text-center text-sm text-slate-500">No violations on file.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Accidents */}
      <div className="hs-card" data-testid="accidents-card">
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <div>
            <div className="hs-overline inline-flex items-center gap-2"><Car size={12}/> CRASHES & CLAIMS</div>
            <h3 className="font-display font-bold text-xl mt-1">Accidents ({accs.length})</h3>
          </div>
          {isAdmin && (
            <button onClick={() => { setEditingAcc(null); setShowAccModal(true); }} className="hs-btn-primary inline-flex items-center gap-2" data-testid="add-accident-button">
              <Plus size={14}/> LOG ACCIDENT
            </button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-left">
              <tr>
                <Th>Date</Th><Th>Location</Th><Th>Driver</Th><Th>Severity</Th><Th>Fault</Th>
                <Th>Inj/Fat</Th><Th>Damage</Th><Th>Claim</Th><Th></Th>
              </tr>
            </thead>
            <tbody>
              {accs.map((a) => {
                const drv = drivers.find((d) => d.id === a.driver_id);
                return (
                  <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`accident-row-${a.id}`}>
                    <td className="px-4 py-3 font-mono text-xs">{(a.date || "").slice(0, 10)}</td>
                    <td className="px-4 py-3 inline-flex items-center gap-1"><MapPin size={12} className="text-slate-400"/> {a.location}</td>
                    <td className="px-4 py-3 text-xs">{drv ? drv.name : <span className="text-slate-400">—</span>}</td>
                    <td className="px-4 py-3"><span className={`hs-tag ${a.severity === "fatal" ? "hs-tag-danger" : (SEVERITY_TAG[a.severity] || "hs-tag-info")}`}>{(a.severity || "minor").toUpperCase()}</span></td>
                    <td className="px-4 py-3 text-xs uppercase">
                      {a.fault === "at_fault" ? <span className="hs-tag hs-tag-danger">AT FAULT</span> :
                       a.fault === "not_at_fault" ? <span className="hs-tag hs-tag-success">NOT AT FAULT</span> :
                       <span className="hs-tag hs-tag-info">UNKNOWN</span>}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{a.injuries || 0}/{a.fatalities || 0}</td>
                    <td className="px-4 py-3 font-mono">${(a.vehicle_damage_estimate || 0).toLocaleString()}</td>
                    <td className="px-4 py-3"><span className="hs-tag hs-tag-info">{(a.claim_status || "open").toUpperCase()}</span></td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {isAdmin && (
                        <>
                          <button onClick={() => { setEditingAcc(a); setShowAccModal(true); }} className="hs-btn-ghost p-2" title="Edit"><Edit3 size={13}/></button>
                          <button onClick={() => removeAcc(a.id)} className="hs-btn-ghost p-2" title="Delete"><Trash2 size={13}/></button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
              {accs.length === 0 && !loading && (
                <tr><td colSpan={9} className="p-8 text-center text-sm text-slate-500">No accidents recorded.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Driver Risk Board */}
      <div className="hs-card" data-testid="driver-risk-card">
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <div>
            <div className="hs-overline inline-flex items-center gap-2"><TrendingDown size={12}/> PROBLEM DRIVERS</div>
            <h3 className="font-display font-bold text-xl mt-1">Driver Risk Board</h3>
            <p className="text-xs text-slate-500 mt-1">Drivers ranked by risk score (lower = higher risk). Linked from inspections, violations and accidents.</p>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-left">
              <tr>
                <Th>#</Th><Th>Driver</Th><Th>Status</Th><Th>Risk</Th><Th>Grade</Th>
                <Th>Violations</Th><Th>Open</Th><Th>Inspections</Th><Th>OOS</Th><Th>Accidents</Th><Th>At-fault</Th>
              </tr>
            </thead>
            <tbody>
              {riskRows.map((r, idx) => {
                const color = r.risk_score >= 85 ? "#059669" : r.risk_score >= 70 ? "#D97706" : "#DC2626";
                return (
                  <tr key={r.driver_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`driver-risk-row-${r.driver_id}`}>
                    <td className="px-4 py-3 font-mono text-xs text-slate-400">{idx + 1}</td>
                    <td className="px-4 py-3 font-medium inline-flex items-center gap-2">
                      {r.risk_score < 70 && <UserX size={13} className="text-red-500"/>}
                      {r.driver_name}
                    </td>
                    <td className="px-4 py-3 text-xs uppercase">{r.status}</td>
                    <td className="px-4 py-3 font-mono font-bold" style={{ color }} data-testid={`driver-risk-score-${r.driver_id}`}>{r.risk_score}</td>
                    <td className="px-4 py-3"><span className="hs-tag" style={{ borderColor: color, color }}>{r.grade}</span></td>
                    <td className="px-4 py-3 font-mono">{r.violation_count}</td>
                    <td className="px-4 py-3 font-mono text-amber-700">{r.open_violations}</td>
                    <td className="px-4 py-3 font-mono">{r.inspection_count}</td>
                    <td className="px-4 py-3 font-mono text-red-700">{r.oos_count}</td>
                    <td className="px-4 py-3 font-mono">{r.accident_count}</td>
                    <td className="px-4 py-3 font-mono text-red-700">{r.at_fault_count}</td>
                  </tr>
                );
              })}
              {riskRows.length === 0 && !loading && (
                <tr><td colSpan={11} className="p-8 text-center text-sm text-slate-500">No drivers to rank yet. Add drivers and link them to incidents.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showInspModal && (
        <InspectionModal
          initial={editingInsp}
          clientId={adminClientId || (isAdmin ? null : user?.id)}
          drivers={drivers}
          onClose={() => { setShowInspModal(false); setEditingInsp(null); }}
          onSaved={async () => { setShowInspModal(false); setEditingInsp(null); await load(); toast.success("Inspection saved"); }}
        />
      )}
      {showViolModal && (
        <ViolationModal
          initial={editingViol}
          clientId={adminClientId || (isAdmin ? null : user?.id)}
          drivers={drivers}
          onClose={() => { setShowViolModal(false); setEditingViol(null); }}
          onSaved={async () => { setShowViolModal(false); setEditingViol(null); await load(); toast.success("Violation saved"); }}
        />
      )}
      {showAccModal && (
        <AccidentModal
          initial={editingAcc}
          clientId={adminClientId || (isAdmin ? null : user?.id)}
          drivers={drivers}
          onClose={() => { setShowAccModal(false); setEditingAcc(null); }}
          onSaved={async () => { setShowAccModal(false); setEditingAcc(null); await load(); toast.success("Accident saved"); }}
        />
      )}
    </div>
  );
}

function Th({ children }) { return <th className="px-4 py-3 hs-overline">{children}</th>; }

function Stat({ icon: Icon, label, value, accent = "#1D4ED8" }) {
  return (
    <div className="border border-slate-200 p-3" data-testid={`safety-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-2">
        <Icon size={14} style={{ color: accent }} />
        <span className="hs-overline text-[10px]">{label}</span>
      </div>
      <div className="font-mono font-black text-2xl mt-1" style={{ color: accent }}>{value}</div>
    </div>
  );
}

function Bar({ label, value, testid }) {
  const color = value >= 85 ? "#059669" : value >= 70 ? "#D97706" : "#DC2626";
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-slate-700">{label}</span>
        <span className="font-mono font-bold" style={{ color }}>{value}</span>
      </div>
      <div className="h-1.5 bg-slate-100">
        <div className="h-full transition-all" style={{ width: `${value}%`, background: color }} />
      </div>
    </div>
  );
}

function ScoreRing({ value, grade }) {
  const r = 60, c = 2 * Math.PI * r;
  const offset = c - (value / 100) * c;
  const color = value >= 85 ? "#059669" : value >= 70 ? "#D97706" : "#DC2626";
  return (
    <div className="relative my-4" data-testid="safety-score-ring">
      <svg width="160" height="160" className="-rotate-90">
        <circle cx="80" cy="80" r={r} stroke="#E2E8F0" strokeWidth="10" fill="transparent"/>
        <circle cx="80" cy="80" r={r} stroke={color} strokeWidth="10" fill="transparent"
                strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round"
                style={{ transition: "stroke-dashoffset 0.6s ease" }}/>
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="font-display font-black text-5xl" style={{ color }}>{value}</div>
        <div className="font-mono text-xs tracking-widest text-slate-500">GRADE {grade}</div>
      </div>
    </div>
  );
}

function InspectionModal({ initial, clientId, drivers = [], onClose, onSaved }) {
  const [form, setForm] = useState(initial || {
    date: new Date().toISOString().slice(0, 10),
    level: 1, location: "", violations_count: 0, oos: false, score: 100, notes: "", driver_id: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : (["level", "violations_count", "score"].includes(k) ? Number(e.target.value) : e.target.value);
    setForm({ ...form, [k]: v });
  };
  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        driver_id: form.driver_id || null,
        date: form.date.length === 10 ? new Date(form.date + "T12:00:00Z").toISOString() : form.date,
      };
      if (initial?.id) await api.put(`/inspections/${initial.id}`, payload);
      else await api.post(`/inspections${clientId ? `?client_id=${clientId}` : ""}`, payload);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="hs-card p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="inspection-modal">
        <div className="hs-overline">{initial ? "EDIT" : "LOG"} INSPECTION</div>
        <h3 className="font-display font-bold text-2xl mt-1">DOT Roadside</h3>
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          <Field label="Date" type="date" value={(form.date || "").slice(0, 10)} onChange={set("date")} testid="insp-date"/>
          <div>
            <label className="hs-overline">Level</label>
            <select className="hs-input mt-2" value={form.level} onChange={set("level")} data-testid="insp-level">
              {[1, 2, 3, 4, 5, 6].map((l) => <option key={l} value={l}>Level {l}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="hs-overline">Driver (optional — link for risk tracking)</label>
            <select className="hs-input mt-2" value={form.driver_id || ""} onChange={set("driver_id")} data-testid="insp-driver">
              <option value="">— Unassigned —</option>
              {drivers.map((d) => <option key={d.id} value={d.id}>{d.name}{d.cdl_number ? ` · ${d.cdl_number}` : ""}</option>)}
            </select>
          </div>
          <Field label="Location" value={form.location} onChange={set("location")} className="sm:col-span-2" testid="insp-location"/>
          <Field label="Violations Count" type="number" value={form.violations_count} onChange={set("violations_count")} testid="insp-vcount"/>
          <Field label="Score (0-100)" type="number" value={form.score} onChange={set("score")} testid="insp-score"/>
          <label className="flex items-center gap-2 mt-2 sm:col-span-2 text-sm">
            <input type="checkbox" checked={!!form.oos} onChange={set("oos")} data-testid="insp-oos" /> Out-of-Service
          </label>
          <div className="sm:col-span-2">
            <label className="hs-overline">Notes</label>
            <textarea className="hs-input mt-2 min-h-[64px]" value={form.notes || ""} onChange={set("notes")} data-testid="insp-notes"/>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="hs-btn-secondary">CANCEL</button>
          <button onClick={save} disabled={busy} className="hs-btn-primary" data-testid="insp-save">{busy ? "SAVING…" : "SAVE"}</button>
        </div>
      </div>
    </div>
  );
}

function ViolationModal({ initial, clientId, drivers = [], onClose, onSaved }) {
  const [form, setForm] = useState(initial || {
    date: new Date().toISOString().slice(0, 10),
    code: "", description: "", severity: "minor", basic: "vehicle_maintenance",
    status: "open", fine: 0, notes: "", driver_id: "",
    challenged: false, challenge_status: "not_challenged", challenge_notes: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => {
    const v = k === "fine" ? Number(e.target.value || 0)
      : k === "challenged" ? e.target.checked
      : e.target.value;
    setForm({ ...form, [k]: v });
  };
  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        driver_id: form.driver_id || null,
        challenge_status: form.challenged ? (form.challenge_status || "pending") : "not_challenged",
        date: form.date.length === 10 ? new Date(form.date + "T12:00:00Z").toISOString() : form.date,
      };
      if (initial?.id) await api.put(`/violations/${initial.id}`, payload);
      else await api.post(`/violations${clientId ? `?client_id=${clientId}` : ""}`, payload);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="hs-card p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="violation-modal">
        <div className="hs-overline">{initial ? "EDIT" : "LOG"} VIOLATION</div>
        <h3 className="font-display font-bold text-2xl mt-1">Citation</h3>
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          <Field label="Date" type="date" value={(form.date || "").slice(0, 10)} onChange={set("date")} testid="viol-date"/>
          <Field label="Code (e.g. 393.9)" value={form.code} onChange={set("code")} testid="viol-code"/>
          <div className="sm:col-span-2">
            <label className="hs-overline">Driver (optional — link for risk tracking)</label>
            <select className="hs-input mt-2" value={form.driver_id || ""} onChange={set("driver_id")} data-testid="viol-driver">
              <option value="">— Unassigned —</option>
              {drivers.map((d) => <option key={d.id} value={d.id}>{d.name}{d.cdl_number ? ` · ${d.cdl_number}` : ""}</option>)}
            </select>
          </div>
          <Field label="Description" value={form.description} onChange={set("description")} className="sm:col-span-2" testid="viol-desc"/>
          <div>
            <label className="hs-overline">BASIC Category</label>
            <select className="hs-input mt-2" value={form.basic} onChange={set("basic")} data-testid="viol-basic">
              {Object.entries(BASIC_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="hs-overline">Severity</label>
            <select className="hs-input mt-2" value={form.severity} onChange={set("severity")} data-testid="viol-sev">
              <option value="minor">Minor</option>
              <option value="moderate">Moderate</option>
              <option value="severe">Severe</option>
            </select>
          </div>
          <div>
            <label className="hs-overline">Status</label>
            <select className="hs-input mt-2" value={form.status} onChange={set("status")} data-testid="viol-status">
              <option value="open">Open</option>
              <option value="paid">Paid</option>
              <option value="contested">Contested</option>
              <option value="dismissed">Dismissed</option>
            </select>
          </div>
          <Field label="Fine ($)" type="number" value={form.fine} onChange={set("fine")} testid="viol-fine"/>
          <div className="sm:col-span-2 border-t border-slate-200 pt-3 mt-1">
            <div className="hs-overline text-blue-700 mb-2">CHALLENGE / DISPUTE</div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={!!form.challenged} onChange={set("challenged")} data-testid="viol-challenged" />
              We are challenging this ticket
            </label>
          </div>
          {form.challenged && (
            <>
              <div className="sm:col-span-2">
                <label className="hs-overline">Challenge Status</label>
                <select className="hs-input mt-2" value={form.challenge_status} onChange={set("challenge_status")} data-testid="viol-challenge-status">
                  <option value="pending">Pending</option>
                  <option value="won">Won (dismissed/reduced)</option>
                  <option value="lost">Lost</option>
                  <option value="withdrawn">Withdrawn</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="hs-overline">Challenge Notes</label>
                <textarea className="hs-input mt-2 min-h-[48px]" value={form.challenge_notes || ""} onChange={set("challenge_notes")} data-testid="viol-challenge-notes"/>
              </div>
            </>
          )}
          <div className="sm:col-span-2">
            <label className="hs-overline">Notes</label>
            <textarea className="hs-input mt-2 min-h-[64px]" value={form.notes || ""} onChange={set("notes")} data-testid="viol-notes"/>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="hs-btn-secondary">CANCEL</button>
          <button onClick={save} disabled={busy} className="hs-btn-primary" data-testid="viol-save">{busy ? "SAVING…" : "SAVE"}</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, testid, className = "", ...rest }) {
  return (
    <div className={className}>
      <label className="hs-overline">{label}</label>
      <input className="hs-input mt-2" data-testid={testid} {...rest}/>
    </div>
  );
}


function AccidentModal({ initial, clientId, drivers = [], onClose, onSaved }) {
  const [form, setForm] = useState(initial || {
    date: new Date().toISOString().slice(0, 10),
    location: "", severity: "minor", fault: "unknown", claim_status: "open",
    fatalities: 0, injuries: 0, vehicle_damage_estimate: 0,
    description: "", driver_id: "", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => {
    const numKeys = ["fatalities", "injuries", "vehicle_damage_estimate"];
    const v = numKeys.includes(k) ? Number(e.target.value || 0) : e.target.value;
    setForm({ ...form, [k]: v });
  };
  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        driver_id: form.driver_id || null,
        date: form.date.length === 10 ? new Date(form.date + "T12:00:00Z").toISOString() : form.date,
      };
      if (initial?.id) await api.put(`/accidents/${initial.id}`, payload);
      else await api.post(`/accidents${clientId ? `?client_id=${clientId}` : ""}`, payload);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="hs-card p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="accident-modal">
        <div className="hs-overline">{initial ? "EDIT" : "LOG"} ACCIDENT</div>
        <h3 className="font-display font-bold text-2xl mt-1">Crash / Claim</h3>
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          <Field label="Date" type="date" value={(form.date || "").slice(0, 10)} onChange={set("date")} testid="acc-date"/>
          <Field label="Location" value={form.location} onChange={set("location")} testid="acc-location"/>
          <div className="sm:col-span-2">
            <label className="hs-overline">Driver (optional)</label>
            <select className="hs-input mt-2" value={form.driver_id || ""} onChange={set("driver_id")} data-testid="acc-driver">
              <option value="">— Unassigned —</option>
              {drivers.map((d) => <option key={d.id} value={d.id}>{d.name}{d.cdl_number ? ` · ${d.cdl_number}` : ""}</option>)}
            </select>
          </div>
          <div>
            <label className="hs-overline">Severity</label>
            <select className="hs-input mt-2" value={form.severity} onChange={set("severity")} data-testid="acc-severity">
              <option value="minor">Minor</option>
              <option value="moderate">Moderate</option>
              <option value="severe">Severe</option>
              <option value="fatal">Fatal</option>
            </select>
          </div>
          <div>
            <label className="hs-overline">Fault</label>
            <select className="hs-input mt-2" value={form.fault} onChange={set("fault")} data-testid="acc-fault">
              <option value="unknown">Unknown</option>
              <option value="at_fault">At Fault</option>
              <option value="not_at_fault">Not At Fault</option>
            </select>
          </div>
          <Field label="Injuries" type="number" value={form.injuries} onChange={set("injuries")} testid="acc-injuries"/>
          <Field label="Fatalities" type="number" value={form.fatalities} onChange={set("fatalities")} testid="acc-fatalities"/>
          <Field label="Vehicle Damage ($)" type="number" value={form.vehicle_damage_estimate} onChange={set("vehicle_damage_estimate")} testid="acc-damage"/>
          <div>
            <label className="hs-overline">Claim Status</label>
            <select className="hs-input mt-2" value={form.claim_status} onChange={set("claim_status")} data-testid="acc-claim">
              <option value="open">Open</option>
              <option value="in_review">In Review</option>
              <option value="paid">Paid</option>
              <option value="denied">Denied</option>
              <option value="closed">Closed</option>
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="hs-overline">Description</label>
            <textarea className="hs-input mt-2 min-h-[64px]" value={form.description || ""} onChange={set("description")} data-testid="acc-desc"/>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="hs-btn-secondary">CANCEL</button>
          <button onClick={save} disabled={busy} className="hs-btn-primary" data-testid="acc-save">{busy ? "SAVING…" : "SAVE"}</button>
        </div>
      </div>
    </div>
  );
}
